"use client";

import type { FooterData, FooterGroup, LanguageOption } from "../../../types";
import { RuntimeImage, useLocale } from "../../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { t } from "../../../i18n/strings";
import { resolveConfiguredHref } from "../../../utils/links";
import { SocialDiscRow } from "./SocialDiscRow";
import { FooterLanguageLinks } from "./FooterLanguageLinks";

/**
 * Desktop footer — four columns:
 *   [Logo + primary address] [Visitez aussi nos pages] [Contact note] [Informations pratiques + socials]
 *
 * All copy is sourced from `FooterData` (CMS), so reordering, renaming, or
 * localizing happens through `siteSettings.json` rather than code edits.
 * Group titles are matched by their display title, but the renderer falls
 * back to the array order if titles change — making the layout resilient
 * to editorial wording tweaks.
 */
export function SiteFooterDesktop({
	data,
	logoSrc,
	logoAlt,
	language,
	languageOptions,
	onLanguageChange,
	resolveHref,
}: {
	data: FooterData;
	logoSrc: string;
	logoAlt: string;
	language?: string;
	languageOptions?: LanguageOption[];
	onLanguageChange?: (code: string) => void;
	resolveHref?: (code: string) => string;
}) {
	const { previewMode } = useDevPlaceholder();
	const locale = useLocale();
	const resourcesGroup = pickGroup(data.groups, ["Visitez aussi nos pages", "Visitez aussi"], 0);
	const practicalGroup = pickGroup(data.groups, ["Informations pratiques"], 1);
	const primaryAddress = data.addresses[0];
	const secondaryAddresses = data.addresses.slice(1);
	const contactNote = (data.contactNote ?? "").trim();
	// Production hides links with no destination; preview keeps them (clicking opens the dev modal).
	const keep = (href: unknown) => previewMode || Boolean(resolveConfiguredHref(href as never));
	const primaryActions = (primaryAddress?.actions ?? []).filter((action) => keep(action.href));
	const legalLinks = data.legalLinks.filter((link) => keep(link.href));

	return (
		// Bascule à lg (1024px) et NON à md : entre les deux, la grille n'a qu'une
		// colonne et ce pied de page « bureau » s'empilait verticalement sans le
		// confort de l'accordéon — sous lg c'est la variante mobile qui sert.
		<div className="hidden flex-col gap-10 lg:flex">
			<div className="grid gap-10 lg:grid-cols-[minmax(220px,260px)_minmax(180px,1fr)_minmax(220px,1fr)_minmax(220px,260px)] lg:items-start">
				{/* Column 1 — Brand + addresses */}
				<div className="space-y-6 text-sm xl:text-base text-white/85">
					{/* Lockup blanc sur une ligne : la hauteur est le facteur limitant (45px max),
					    la largeur suit le ratio de l'image. */}
					<RuntimeImage src={logoSrc} alt={logoAlt} className="h-auto max-h-[45px] w-auto max-w-full object-contain object-left" />
					{primaryAddress && (
						<div>
							<p className="text-base xl:text-lg font-semibold text-white">{primaryAddress.title}</p>
							<div className="mt-2 space-y-1">
								{primaryAddress.lines.map((line, idx) => (
									<p key={idx}>{line}</p>
								))}
							</div>
							{primaryActions.length ? (
								<div className="mt-3 flex flex-wrap gap-2 text-xs xl:text-sm">
									{primaryActions.map((action) => (
										<GuardedLink
											key={action.label}
											label={action.label}
											href={action.href}
											className="inline-flex items-center rounded-full border border-white/20 px-3 py-1 font-medium text-white hover:bg-white/10"
										>
											{action.label}
										</GuardedLink>
									))}
								</div>
							) : null}
						</div>
					)}
					{secondaryAddresses.length > 0 && (
						<div className="space-y-4">
							{secondaryAddresses.map((addr) => (
								<div key={addr.title}>
									<p className="font-semibold text-white">{addr.title}</p>
									<div className="mt-1 space-y-1">
										{addr.lines.map((line, idx) => (
											<p key={idx}>{line}</p>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Colonnes 2 à 4 — décalées vers le bas pour poser les intitulés sur la
				    ligne du logo (le logo, lui, ne bouge pas). Trois valeurs et non une :
				    l'image du lockup se réduit quand la colonne rétrécit (45px de haut au
				    delà de 1100px, 39px à 1024px), donc son encre remonte alors que les
				    intitulés, eux, gardent leur corps. Valeurs calées au pixel sur l'encre
				    réelle, pas sur les boîtes. */}
				{/* Column 2 — Visitez aussi nos pages */}
				<FooterLinkColumn group={resourcesGroup} fallbackTitle="Visitez aussi nos pages" className="min-[1024px]:pt-[7px] min-[1100px]:pt-[9px] min-[1280px]:pt-[8px]" />

				{/* Column 3 — Contact note paragraph */}
				<div className="text-sm leading-relaxed text-white/90 min-[1024px]:pt-[7px] min-[1100px]:pt-[9px] min-[1280px]:pt-[8px] xl:text-base">
					{contactNote ? <p>{contactNote}</p> : null}
				</div>

				{/* Column 4 — Informations pratiques + socials */}
				<div className="flex flex-col gap-6 min-[1024px]:pt-[7px] min-[1100px]:pt-[9px] min-[1280px]:pt-[8px]">
					<FooterLinkColumn group={practicalGroup} fallbackTitle="Informations pratiques" />
					<SocialDiscRow items={data.socials} />
				</div>
			</div>

			<div className="border-t border-white/20 pt-6 text-sm text-white/80">
				{/* Bottom bar — centered & stacked, identical to the tablet/mobile layout (per the
				    reference image): copyright on top, the partner logos centered below, then the
				    FR · EN · LN switch. Held at one consistent text size (text-sm) across all widths. */}
				<div className="flex flex-col items-center gap-4 text-center">
					{/* Crédit prestataire retiré à la demande du client : le bas de page ne porte
					    plus que le copyright BCC. Les liens légaux se glissent discrètement à la
					    suite du copyright, dans le même corps de texte (plus de rangée majuscules). */}
					<p>
						© {new Date().getFullYear()} Banque Centrale du Congo. {t(locale, "allRightsReserved")}
						{legalLinks.map((link) => (
							<span key={link.label}>
								{" · "}
								<GuardedLink label={link.label} href={link.href} className="hover:text-white">
									{link.label}
								</GuardedLink>
							</span>
						))}
					</p>
					{languageOptions && onLanguageChange ? (
						<FooterLanguageLinks
							language={language ?? ""}
							languageOptions={languageOptions}
							onLanguageChange={onLanguageChange}
							resolveHref={resolveHref}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
}

/** Renders one footer link column: bold title + vertical list. Hidden when
 *  the group is empty so the grid collapses gracefully. */
function FooterLinkColumn({ group, fallbackTitle, className }: { group: FooterGroup | null; fallbackTitle: string; className?: string }) {
	const { previewMode } = useDevPlaceholder();
	const links = (group?.links ?? []).filter((link) => previewMode || resolveConfiguredHref(link.href));
	if (!group || links.length === 0) return <div />;
	return (
		<div className={className}>
			<h5 className="text-base xl:text-lg font-semibold text-white">{group.title || fallbackTitle}</h5>
			<ul className="mt-4 space-y-2 text-sm xl:text-base text-white/85">
				{links.map((link) => (
					<li key={link.label}>
						<GuardedLink label={link.label} className="hover:underline" href={link.href}>
							{link.label}
						</GuardedLink>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Find a group by trying any of the candidate titles, falling back to the
 *  Nth group by position when nothing matches. Lets the CMS rename groups
 *  without breaking the layout. */
function pickGroup(groups: FooterGroup[], titles: string[], fallbackIndex: number): FooterGroup | null {
	for (const title of titles) {
		const match = groups.find((g) => g.title === title);
		if (match) return match;
	}
	return groups[fallbackIndex] ?? null;
}
